import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

// Elements the focus trap treats as tabbable. Intentionally simple — covers
// everything the design system renders inside modals today. Queried fresh on
// every Tab press so dynamically shown/hidden controls stay trapped correctly.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// ── Modal stack ──────────────────────────────────────────────────────────────
// Modals STACK (the work viewer opens over the score-anatomy modal, over the
// matrix cell modal), which breaks every per-instance assumption at once:
// - Body lock: a save-and-restore of body.style.overflow only survives strict
//   LIFO closing. Both modals listen for Escape at document level, so one
//   press used to close BOTH in the same commit — whichever cleaned up second
//   restored its stale 'hidden' and the page scroll died. The lock is
//   therefore REFCOUNTED at module level: first modal in saves and hides,
//   last modal out restores, close order irrelevant.
// - Escape: only the TOP modal may act, so one press peels one layer.
// - Tab trap: only the TOP modal may trap, or two document-level handlers
//   fight over focus and Tab stops advancing.
const modalStack: symbol[] = [];
let prevBodyOverflow = '';

function pushModal(token: symbol) {
  if (modalStack.length === 0) {
    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  modalStack.push(token);
}

function popModal(token: symbol) {
  const i = modalStack.indexOf(token);
  if (i !== -1) modalStack.splice(i, 1);
  if (modalStack.length === 0) document.body.style.overflow = prevBodyOverflow;
}

function isTopModal(token: symbol) {
  return modalStack[modalStack.length - 1] === token;
}

// Lightweight modal: portaled to <body>, animated backdrop + panel, closes on
// Escape or backdrop click, and locks body scroll while open. Self-scopes `.ds`.
//
// Focus management (a11y): on open, focus moves to the panel itself; while
// open, Tab / Shift+Tab cycle within the panel (hand-rolled trap, no deps);
// on close, focus is restored to whatever was focused before the modal opened.
export function Modal({
  open,
  onClose,
  children,
  className,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  labelledBy?: string;
}) {
  const [mounted, setMounted] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);
  // Stable identity of THIS modal instance on the stack.
  const tokenRef = React.useRef<symbol | null>(null);
  if (tokenRef.current === null) tokenRef.current = Symbol('ds-modal');
  const token = tokenRef.current;
  React.useEffect(() => setMounted(true), []);

  // Stack membership + body lock, keyed on `open` ALONE: an unstable onClose
  // (inline arrow in the host) must not cycle the lock on every host render.
  React.useEffect(() => {
    if (!open) return;
    pushModal(token);
    return () => popModal(token);
  }, [open, token]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Top of the stack only: with stacked modals, one Escape peels one
      // layer instead of closing everything at once. preventDefault so a
      // window-level Esc listener downstream (e.g. the disc) can see the
      // event was consumed here and not also back itself out.
      if (e.key === 'Escape' && isTopModal(token)) {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, token]);

  // Focus management: initial focus, Tab trap, and restore-on-close. Keyed on
  // `mounted` too — if the modal is open on first render the portal doesn't
  // exist until the mount effect flips state, so this must re-run to reach the
  // panel node.
  React.useEffect(() => {
    if (!open || !mounted) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Initial focus goes to the panel itself (tabIndex={-1}) rather than the
    // first focusable control: screen readers announce the dialog label, and a
    // tall panel isn't scrolled to some mid-content button. preventScroll on
    // every focus call below means the trap never scrolls the page, which also
    // keeps us safe under prefers-reduced-motion.
    panelRef.current?.focus({ preventScroll: true });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      // A buried modal must not trap: two active traps fight over focus and
      // Tab never advances. The top modal owns the keyboard.
      if (!isTopModal(token)) return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        // Skip elements hidden via display:none (e.g. inactive tab content).
        (el) => el.getClientRects().length > 0
      );
      if (focusables.length === 0) {
        // Nothing tabbable inside: keep focus pinned on the panel.
        e.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return; // unreachable (length checked); satisfies noUncheckedIndexedAccess
      const active = document.activeElement;
      // Cycle at the edges. If focus somehow escaped the panel (or is still on
      // the panel wrapper going backwards), recapture it at the matching edge.
      if (e.shiftKey) {
        if (active === first || active === panel || !panel.contains(active)) {
          e.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (active === last || !panel.contains(active)) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Restore focus to wherever the user was before the modal opened. If
      // that element left the DOM in the meantime this is a harmless no-op.
      previouslyFocused?.focus({ preventScroll: true });
    };
  }, [open, mounted, token]);

  if (!mounted) return null;

  // Mosaico has no motion library; the enter animation is CSS (arcdisk.css
  // `.arcdisk-modal-*` keyframes) and there is no exit animation.
  if (!open) return null;
  return createPortal(
    <div className="ds fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
          <div
            className="arcdisk-modal-backdrop absolute inset-0 bg-foreground/40 backdrop-blur-[2px]"
            onClick={onClose}
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            // Programmatically focusable so the panel can take initial focus
            // (and act as the trap's anchor) without joining the Tab order.
            tabIndex={-1}
            className={cn(
              'arcdisk-modal-panel',
              // outline-none: the panel receives programmatic focus on open;
              // the dialog chrome itself shouldn't render a focus ring.
              'relative z-10 flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-2xl outline-none',
              className
            )}
          >
            {children}
          </div>
    </div>,
    document.body
  );
}
